import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";

import memAgentIntroMacosImageUrl from "@/assets/images/onboarding/mem-agent-intro/mem-agent-intro-macos.png";
import memAgentIntroWindowsImageUrl from "@/assets/images/onboarding/mem-agent-intro/mem-agent-intro-windows.png";
import type { StepRenderProps } from "@/components/onboarding/components/welcome-wizard";
import {
  FooterButton,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { clientEnvModule } from "@/modules/client-env";

export const MemAgentIntroStep = observer(({ onComplete }: StepRenderProps) => (
  <MemAgentIntroStepView isWindows={clientEnvModule.isWindows()} onComplete={onComplete} />
));

export interface MemAgentIntroStepViewProps {
  isWindows?: boolean;
  onComplete: () => void;
}

/** Introduces new onboarding users to Mem Agent before the Proactive trial offer. */
export const MemAgentIntroStepView = ({
  isWindows = false,
  onComplete,
}: MemAgentIntroStepViewProps) => (
  <IntroStepWrapper>
    <IntroTitle
      variant={MDSTextVariant.HeaderL}
      weight={MDSTextWeight.SemiBold}
      color={MDSTextColor.Contrast}
    >
      Meet Mem Agent
    </IntroTitle>

    <IntroSlot>
      {isWindows ? <WindowsIntroMedia /> : <MacosIntroMedia />}

      <IntroCopyGroup>
        <IntroCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
          Mem Agent works off everything you put in Mem — notes, meetings, quick thoughts. Ask and
          it finds the detail, catches you up, and tells you what’s next.
        </IntroCopy>
        <IntroCopy variant={MDSTextVariant.ParagraphSm} color={MDSTextColor.Primary}>
          It also keeps an eye on your follow-ups, deadlines, and loose ends — and reminds you at
          just the right moment.
        </IntroCopy>
      </IntroCopyGroup>
    </IntroSlot>

    <IntroFooter>
      <FooterButton onPress={onComplete}>Next</FooterButton>
    </IntroFooter>
  </IntroStepWrapper>
);

const MacosIntroMedia = () => (
  <IntroMediaFrame>
    <MacosIntroAspect>
      <MacosIntroImage src={memAgentIntroMacosImageUrl} alt="Mem Agent response preview" />
    </MacosIntroAspect>
  </IntroMediaFrame>
);

const WindowsIntroMedia = () => (
  <WindowsMediaFrame>
    <WindowsIntroImage src={memAgentIntroWindowsImageUrl} alt="Mem Agent response preview" />
  </WindowsMediaFrame>
);

const IntroStepWrapper = styled(WizardContentWrapper)`
  gap: 0;
  max-height: calc(
    min(720px, 95dvh) - ${({ theme }) => theme.spacing[7]} - ${({ theme }) => theme.spacing[7]}
  );
  overflow: hidden;
`;

const IntroTitle = styled(MDSText)`
  display: block;
  flex: 0 0 auto;
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  word-break: break-word;
`;

const IntroSlot = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 20px;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing[2]} 0;
`;

const IntroMediaFrame = styled.div`
  flex: 0 0 auto;
  width: 100%;
  height: 154px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const MacosIntroAspect = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1354 / 682;
  overflow: hidden;
`;

const MacosIntroImage = styled.img`
  position: absolute;
  top: -31.79%;
  left: 0;
  width: 100%;
  height: 105.84%;
  object-fit: fill;
`;

const WindowsMediaFrame = styled.div`
  flex: 0 0 auto;
  width: 100%;
  aspect-ratio: 474 / 265;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface.modal};
`;

const WindowsIntroImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 474 / 265;
`;

const IntroCopyGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const IntroCopy = styled(MDSText)`
  display: block;
  width: 100%;
`;

const IntroFooter = styled.div`
  display: flex;
  flex: 0 0 auto;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing[1]};
`;
